<template>
  <main>
    <div class="container">
      <div v-if="isActionEmailConfirmation" class="row">
        <div class="col">
          <h1 class="color-orange-high">
            {{ $t('entry.emailVerification.heading') }}
            <span class="color-foreground">{{ $t('entry.emailVerification.subheading') }}</span>
          </h1>
        </div>
      </div>
      <div v-else class="row margin-bottom-sm">
        <div class="col">
          <h1 class="display-lg color-orange-high">{{ $t('entry.clickToPay') }}</h1>
          <h2 class="display-lg">{{ $t('entry.clickToPayHeading') }}</h2>
        </div>
      </div>
      <form novalidate class="container-fluid" data-test="entry-form" @submit.prevent="submit">
        <div v-if="!isActionEmailConfirmation" class="row">
          <div class="col-full">
            <p>{{ $t('entry.contactInputHeading') }}</p>
          </div>
        </div>
        <al-notify v-if="manualError" error name="login-manual-error">
          {{ $t(manualError) }}
        </al-notify>
        <div class="row">
          <div class="col">
            <al-input
              v-if="isActionEmailConfirmation"
              v-model="userId.value"
              v-validate="'required|email'"
              :label="$t('placeholder.email')"
              :manual-error="errorToken ? $t(errorToken) : null"
              :subtext="$t('common.contactInputSubtext')"
              autocomplete="email"
              type="email"
              name="userId"
              data-test="user-id" />
            <al-contact-input
              v-else
              v-model="userId"
              :label="$t('placeholder.emailOrMobile')"
              :phone-label="$t('placeholder.mobileNumber')"
              :email-label="$t('placeholder.email')"
              :manual-error="errorToken ? $t(errorToken) : null"
              :subtext="$t('entry.contactInputSubtext')"
              name="userId"
              is-dynamic
              :countries="countryOptions"
              data-test="user-id" />
          </div>
        </div>
        <div class="row-vertical-align-bottom">
          <div class="col-full">
            <al-button
              v-analytics:click="$EVENTS.CONTINUE_BUTTON"
              :disabled="hasValidationErrors"
              :loading="loading"
              :label="$t(button)"
              name="form-submit-button"
              uppercase />
          </div>
        </div>
      </form>
    </div>
  </main>
</template>

<script>
import { AlButton, AlContactInput, AlInput, AlNotify } from '@mc-alberta/alcapone'
import nudetect from '@mc-alberta/nudetect'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import useFormValidation from '@/composables/useFormValidation'
import { ACTION, IDENTITY_TYPE, LOADERS, ROUTES } from '@/constants'
import * as types from '@/store/mutation-types'

export default {
  name: ROUTES.ENTRY,
  components: { AlButton, AlContactInput, AlInput, AlNotify },
  setup() {
    const { hasValidationErrors } = useFormValidation()
    return { hasValidationErrors }
  },
  data() {
    const prefillEmailValue = this.$route?.params?.email || ''

    return {
      countryOptions: this.$i18nData.getPhonesConfig(),
      errorToken: '',
      userId: {
        country: this.$store.state.user.consumer.countryCode,
        value: prefillEmailValue,
        type: 'email'
      }
    }
  },
  computed: {
    ...mapState({
      sessionId: (state) => state.identity.lookupSessionId,
      loading: (state) => state.actionIsPending,
      countryCode: (state) => state.user.consumer.countryCode,
      postLoginPath: (state) => state.postLoginPath,
      unprovisionedCards: (state) => state.cards.unprovisionedCards,
      isActionEmailConfirmation: (state) => state.action === ACTION.EMAIL_CONFIRMATION
    }),
    ...mapGetters(['serviceErrorResponse', 'userMustAcceptPolicy']),
    consumerData() {
      if (this.userId.type === 'phone') {
        return {
          channel: 'mobileNumber',
          value: this.userId,
          lookupType: IDENTITY_TYPE.SMS,
          lookupUserId: `${this.userId.countryCode}${this.userId.phoneNumber}`
        }
      }

      return {
        channel: 'emailAddress',
        value: this.userId.value,
        lookupType: IDENTITY_TYPE.EMAIL,
        lookupUserId: this.userId.value
      }
    },
    manualError() {
      return this.$route?.params?.manualError
    },
    button() {
      return this.isActionEmailConfirmation ? 'button.verify' : 'button.continue'
    }
  },
  watch: {
    countryCode(newCountryCode) {
      // Sync default phone country to match locale when no current phone number value is set.
      if (!this.userId.phoneNumber) {
        this.userId.country = newCountryCode
      }
    }
  },
  mounted() {
    this.$validator.validateAll()
    this.resetStepMap()
  },
  methods: {
    ...mapActions([
      'lookupSessionId',
      'clearLookupSessionId',
      'fetchVerificationId',
      'clearServiceLayerError',
      'setRegistrationData',
      'setAction',
      'loadCustomerProfile',
      'stopLoader'
    ]),
    ...mapMutations({
      setActionIsPending: types.SET_ACTION_IS_PENDING,
      setPostLoginPath: types.SET_POST_LOGIN_PATH,
      resetStepMap: types.RESET_STEP_MAP
    }),
    async submit() {
      this.clearLookupSessionId()
      this.clearServiceLayerError()
      this.setActionIsPending(true)

      await this.loginOrRegister()
    },
    async loginOrRegister() {
      this.setRegistrationData({
        consumer: {
          [this.consumerData.channel]: this.consumerData.value
        }
      })

      const isExistingUser = await this.checkExistingUserRegistration()
      if (isExistingUser) {
        await this.initVerification()
        return
      }

      this.setAction(ACTION.REGISTER)
      if (this.postLoginPath) {
        this.setPostLoginPath(null)
      }
      this.$router.push({ name: ROUTES.ADD_CARD, params: { showStepTracker: true } })
    },
    async checkExistingUserRegistration() {
      await this.lookupSessionId({
        userId: this.consumerData.lookupUserId,
        lookupType: this.consumerData.lookupType
      })

      if (this.sessionId) {
        return true
      }

      return false
    },
    async initVerification() {
      await this.fetchVerificationId()

      if (this.handleError()) {
        return
      }

      return this.$router.push({
        name: ROUTES.VERIFY_ACCOUNT,
        params: {
          onSuccess: this.onVerificationSuccess
        }
      })
    },
    async onVerificationSuccess() {
      await this.loadCustomerProfile()

      if (this.handleError()) {
        return this.stopLoader(LOADERS.APP_INIT)
      }

      // we need the registration data during unprovisionedCards flow
      if (!this.userMustAcceptPolicy() && !this.unprovisionedCards.length) {
        this.setRegistrationData(null)
      }

      if (this.isActionEmailConfirmation) {
        this.$router.replace({ name: ROUTES.EMAIL_CONFIRMATION })
      } else {
        this.$router.replace({ name: ROUTES.PROFILE_HOME })
      }

      this.stopLoader(LOADERS.APP_INIT)
    },
    handleError() {
      const error = this.serviceErrorResponse()

      if (!error) {
        return false
      }

      if (error.status === 400) {
        nudetect.beginBehaviouralMonitoring()
        this.errorToken = 'error.default'
      } else {
        this.$router.replace({ name: ROUTES.ERROR })
      }

      this.setRegistrationData(null)
      this.clearLookupSessionId()
      this.setActionIsPending(false)
      return true
    }
  }
}
</script>
